import { request } from '../../core';
import BaseOptions from '../base';
import { SUB_TASK_STATUS } from '@/utils/enum';

export interface SiteInfo {
  siteId: number;
  siteName: string;
  siteMark: number;
  siteIndex: number;
  stopId: number;
  stopName: string;
  routeLineInfoList: RouteLineInfo[];
}
export interface RouteLineInfo {
  routeLineCode: string;
  routeLineName: string;
  startSiteId: number;
  startSiteName: string;
  endSiteId: number;
  endSiteName: string;
  startTime: string;
  endTime: string;
  effect?: boolean;
  passed?: boolean;
}
export interface RouteLineDetail extends RouteLineInfo {
  effect: boolean;
  passed: boolean;
}

export interface SiteDetail {
  id: number;
  taskCode: string;
  subTaskCode: string;
  siteId: number;
  siteName: string;
  siteMark: number; // 1-起点，2-终点，3-途径点
  siteIndex: number;
  stopId: number;
  stopName: string;
  totalCount: number;
  loadCount: number;
  needShow: boolean;
  subStatus?: SUB_TASK_STATUS;
  routeLineList: RouteLineDetail[];
}

export interface CommonLineInfo {
  lineId: number;
  lineName: string;
  siteNameList: string[]; // 站点列表名称
}

export interface CovertRouteLineRes {
  lineId: number;
  lineName: string;
  siteInfoList: (SiteInfo & { id: number })[];
}
class CreateTaskApi extends BaseOptions {
  public createTask(vehicleName: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/createTask',
      method: 'POST',
      headers: this.requestHeaders,
      body: {
        vehicleName: vehicleName
      }
    };
    return request(requestOptions);
  }
  public getVehicleStopList(vehicleName: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL:
        this.fetchDomain + '/schedule/generalTransport/getVehicleStopList',
      method: 'POST',
      headers: this.requestHeaders,
      body: { vehicleName }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param taskCode
   * @returns 获取任务详情
   */
  public getTaskDetailInfo(taskCode: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/getTaskDetailInfo',
      method: 'POST',
      headers: this.requestHeaders,
      body: { taskCode }
    };
    return request(requestOptions);
  }
  /**
   *
   * @returns 获取站点信息列表
   * 返回的res.data类型为{siteId: number, siteName: string}[],其中siteId为站点id，siteName为站点名称
   */
  public getSiteInfoList(): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/getSiteInfoList',
      method: 'POST',
      headers: this.requestHeaders
    };
    return request(requestOptions);
  }
  /**
   *
   * @returns 获取历史站点信息列表
   * 返回的res.data类型为{siteId: number, siteName: string, stopId: number, stopName: string}[],其中siteId为站点id，siteName为站点名称，stopId为停靠点id，stopName为停靠点名称
   */
  public getHistorySiteInfoList(vehicleName: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL:
        this.fetchDomain + '/schedule/spiderWeb/getHistorySiteInfoList',
      method: 'POST',
      headers: this.requestHeaders,
      body: {
        vehicleName
      }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param siteId
   * @returns 获取站点下所有路由线路
   * 返回的res.data类型为{routeLineCode: string, routeLineName: string, startSiteId: number, startSiteName: string, endSiteId: number, endSiteName: string, startTime: string, endTime: string}[],其中routeLineCode为路由线路编码，routeLineName为路由线路名称，startSiteId为起点站点id，startSiteName为起点站点名称，endSiteId为终点站点id，endSiteName为终点站点名称，startTime为起点时间，endTime为终点时间
   */
  public getAllRoutelineListBySiteId(siteId: number): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL:
        this.fetchDomain + '/schedule/spiderWeb/getAllRoutelineListBySiteId',
      method: 'POST',
      headers: this.requestHeaders,
      body: { siteId }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param siteId
   * @returns 获取历史站点下所有路由线路
   * 返回的res.data类型为{routeLineCode: string, routeLineName: string, startSiteId: number, startSiteName: string, endSiteId: number, endSiteName: string, startTime: string, endTime: string}[],其中routeLineCode为路由线路编码，routeLineName为路由线路名称，startSiteId为起点站点id，startSiteName为起点站点名称，endSiteId为终点站点id，endSiteName为终点站点名称，startTime为起点时间，endTime为终点时间
   */
  public getHistoryRoutelineListBySiteId(siteId: number): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL:
        this.fetchDomain +
        '/schedule/spiderWeb/getHistoryRoutelineListBySiteId',
      method: 'POST',
      headers: this.requestHeaders,
      body: { siteId }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param params
   * @returns 添加站点信息
   * 如果用户是第一次选择站点，则需要将选中的站点信息添加到途径点卡片中，且调用该接口，调用成功后调用获取任务详情接口，更新任务数据
   */
  public addSiteInfo(params: {
    taskCode: string;
    siteInfo: Omit<SiteInfo, 'stopId' | 'stopName'>;
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/addSiteInfo',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }
  /**
   *
   * @param params
   * @returns 删除站点信息
   */
  public deleteSiteInfo(params: {
    taskCode: string;
    siteId: number;
    siteIndex: number;
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/deleteSiteInfo',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }
  /**
   *
   * @param params
   * @returns 更新站点信息
   * 如果用户更改了站点，有以下几种情况：
   * 1. 如果用户是更改站点是通过选择站点列表里的站点，则需要判断当前站点下是否有扫描包裹，如果有则需要弹窗提示用户“切换站点后，是否保留已扫描包裹”，true保留 false不保留
   * 2. 如果用户更改站点是通过选择历史站点，则需要将选中的历史站点及停靠点信息添加到途径点卡片中，并要判断当前站点下是否有扫描包裹，如果有则需要弹窗提示用户“切换站点后，是否保留已扫描包裹”，true保留 false不保留
   */
  public updateSiteInfo(params: {
    taskCode: string;
    holdOrders: boolean; // 更新停靠点是否保留订单，true保留 false不保存
    siteInfo: SiteInfo & { id: number };
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/updateSiteInfo',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }

  /**
   *
   * @param params
   * @returns 点击常用线路覆盖现有任务
   * res,data类型为CovertRouteLineRes
   */
  public covertRouteLine(params: {
    taskCode: string;
    lineId: number;
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/covertRouteLine',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }

  /**
   * 路线顺序调整（单项）
   * @param {{
   *     taskCode: string;
   *     id: number;
   *     siteId: number;
   *     siteIndex: number;
   *   }} params
   * @return {*}  {Promise<ApiResponse<null>>}
   * @memberof CreateTaskApi
   */
  public changeLineSort(params: {
    taskCode: string;
    id: number;
    siteId: number;
    siteIndex: number;
  }): Promise<ApiResponse<null>> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/changeLineSort',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }

  /**
   *
   * @param params
   * @returns 保存常用线路
   */
  public saveCommonLine(params: {
    taskCode: string;
    lineName: string;
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/saveCommonLine',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }
  /**
   *
   * @returns 获取常用线路列表
   * 返回的res.data类型为CommonLineInfo[]
   * 展示时需要
   */
  public getCommonLineList(): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/getCommonLineList',
      method: 'POST',
      headers: this.requestHeaders
    };
    return request(requestOptions);
  }
  public editCommonLine(params: {
    lineId: number;
    lineName: string;
  }): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/editCommonLine',
      method: 'POST',
      headers: this.requestHeaders,
      body: params
    };
    return request(requestOptions);
  }
  public deleteCommonLine(lineId: number): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/deleteCommonLine',
      method: 'POST',
      headers: this.requestHeaders,
      body: { lineId }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param vehicleName
   * @returns 校验是否可以发车
   * 点击发车按钮，调用这个接口之前，需要判断现在的任务详情中是否存在以下2种情况：
   * 1. 任务中有缺失必填项信息——没有添加途经点、存在营业部为空、存在停靠点为空，按此顺序优先级依次校验，并出现相应的报错信息：
   * 请添加途经点
   * 存在途经点没有选择营业部，请调整后重试
   * 存在途经点没有选择停靠点，请调整后重试
   * 2.相邻前往的停靠点或营业部相同，报错信息：
   * 存在车辆连续前往同一个营业部或停靠点的情况，请调整后重试
   * 如果这2种情况都不满足，则调用此接口，让服务端继续判断
   * res的结构为{code, message, data}
   * 判断code，已扫单无路线：A072056 未扫单有路线：A072057 选择的线路出发营业部不在此前途径的营业部中：A072058 其他错误，直接从message里取
   * 如果code为A072056，则需要弹窗提示用户“已扫描送至{res.data}的包裹，请选择对应的路由线路后发车”，res.data为营业部名称list，需要用顿号拼接，弹窗按钮分别为“不绑定线路发车”和“我知道了”，用户点击【我知道了】弹窗关闭，用户点击【不绑定线路发车】后出现二次确认弹窗，提示“您确认本次执行的不是蛛网传站任务，无需绑定路由线路？”用户点击【去绑定线路】弹窗关闭，用户点击【不绑定线路发车】后下达发车指令
   * 如果code为A072057，则需要弹窗提示用户“未扫描送至{res.data}的包裹，如确认发车，将不创建路由任务”，res.data为营业部名称list，需要用顿号拼接，弹窗按钮分别为“仍要发车”和“取消”，用户点击【确认发车】后下达发车指令，用户点击【取消】弹窗关闭
   * 如果code为A072058，则需要弹窗提示用户“本趟行程中，没有包含下列路由线路的起点到终点的行程，请调整任务后重试：{res.data}”，res.data为路由线路名称list，需要纵向的排版展示，弹窗按钮为“我知道了”，用户点击【我知道了】弹窗关闭
   * 如果code为其他错误，则需要弹窗提示用户{res.message}，弹窗按钮为“我知道了”，用户点击【我知道了】弹窗关闭
   */
  public checkDepart(vehicleName: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/checkDepart',
      method: 'POST',
      headers: this.requestHeaders,
      body: { vehicleName }
    };
    return request(requestOptions);
  }
  /**
   *
   * @param vehicleName
   * @returns 发车指令
   */
  public vehicleDepart(vehicleName: string): Promise<any> {
    const requestOptions: RequestOptions = {
      absoluteURL: this.fetchDomain + '/schedule/spiderWeb/vehicleDepart',
      method: 'POST',
      headers: this.requestHeaders,
      body: { vehicleName }
    };
    return request(requestOptions);
  }
}

export default CreateTaskApi;
export const createTaskApi = new CreateTaskApi();
